import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Clock, Shield, Target, Heart, ArrowRight } from 'lucide-react';
import BeforeAfterGallery from '../components/BeforeAfterGallery';
import Reviews from '../components/Reviews';

const About = () => {
  const beforeAfterPairs = [
    { before: '/siteimages/Before 1.jpg', after: '/siteimages/after 1.jpg' },
    { before: '/siteimages/before 2.jpg', after: '/siteimages/after 2.jpg' },
    { before: '/siteimages/before 3.jpg', after: '/siteimages/after 3.jpg' },
    { before: '/siteimages/before 4.jpg', after: '/siteimages/after 4.jpg' },
    { before: '/siteimages/before 5.jpg', after: '/siteimages/after 5.jpg' },
    { before: '/siteimages/before 6.jpg', after: '/siteimages/after 6.jpg' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            About <span className="text-bmw-blue">GBW Bodyworks</span>
          </h1>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Your trusted partner for professional car body repair and painting services in Leicestershire.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-6 text-center">
              Professional Service, <span className="text-bmw-blue">Exceptional Results</span>
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              At GBW Bodyworks Limited, we take pride in delivering outstanding car body repair and painting services 
              to vehicle owners throughout Leicestershire. Our team of highly skilled technicians combines years of 
              experience with state-of-the-art equipment to provide repairs that meet the highest industry standards.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              We understand that your vehicle is an important investment, and any damage—whether from a minor scratch 
              or a more significant collision—can be frustrating. That's why we're committed to restoring your vehicle 
              to its pre-accident condition with precision, care, and attention to detail.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our comprehensive range of services includes scratch and paint repair, dent removal, alloy wheel 
              refurbishment, bumper repair, ceramic coating, and stone chip touch-up. Whatever your vehicle needs, 
              we have the expertise and equipment to deliver exceptional results.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-12 text-center">
            Our <span className="text-bmw-blue">Core Values</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Quality Craftsmanship</h3>
              <p className="text-gray-600">
                We never compromise on quality. Every repair is completed to the highest standards using premium 
                materials and proven techniques.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Customer Focus</h3>
              <p className="text-gray-600">
                Your satisfaction is our priority. We provide transparent communication, honest quotes, and 
                exceptional service throughout the repair process.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Precision Work</h3>
              <p className="text-gray-600">
                From color matching to final finishing, we pay meticulous attention to every detail to ensure 
                flawless results.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Guaranteed Results</h3>
              <p className="text-gray-600">
                We stand behind our work with comprehensive guarantees, giving you complete peace of mind with 
                every repair.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Timely Service</h3>
              <p className="text-gray-600">
                We respect your time. Our efficient processes ensure quick turnaround without sacrificing quality 
                or attention to detail.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue text-white rounded-full mb-4">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-dark-navy mb-3">Passionate Team</h3>
              <p className="text-gray-600">
                Our technicians are passionate about automotive repair and take genuine pride in delivering 
                outstanding results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Working Hours Section */}
      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Clock className="w-16 h-16 text-bmw-blue mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Our <span className="text-bmw-blue">Opening Hours</span>
            </h2>
            <p className="text-lg text-soft-grey mb-8">
              Visit us during our opening hours or call to arrange a convenient appointment time.
            </p>

            <div className="bg-dark-teal p-8 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div>
                  <h3 className="text-xl font-bold text-bmw-blue mb-4">Weekdays</h3>
                  <p className="text-lg">Monday - Friday</p>
                  <p className="text-2xl font-bold text-white">09:00 – 17:00</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-bmw-blue mb-4">Weekend</h3>
                  <p className="text-lg">Saturday</p>
                  <p className="text-2xl font-bold text-white">09:00 – 17:00</p>
                  <p className="text-sm text-soft-grey mt-2">Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-8 text-center">
              Why Choose <span className="text-bmw-blue">GBW Bodyworks?</span>
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-bmw-blue rounded-full flex items-center justify-center text-white font-bold">1</div>
                <div>
                  <h3 className="text-xl font-bold text-dark-navy mb-2">Expert Technicians</h3>
                  <p className="text-gray-600">
                    Our team consists of highly trained professionals with extensive experience in all aspects of car body repair and painting.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-bmw-blue rounded-full flex items-center justify-center text-white font-bold">2</div>
                <div>
                  <h3 className="text-xl font-bold text-dark-navy mb-2">Advanced Equipment</h3>
                  <p className="text-gray-600">
                    We invest in the latest technology and tools to ensure we can handle any repair with precision and efficiency.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-bmw-blue rounded-full flex items-center justify-center text-white font-bold">3</div>
                <div>
                  <h3 className="text-xl font-bold text-dark-navy mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">
                    We provide clear, upfront quotes with no hidden fees. You'll know exactly what to expect before we begin work.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-bmw-blue rounded-full flex items-center justify-center text-white font-bold">4</div>
                <div>
                  <h3 className="text-xl font-bold text-dark-navy mb-2">Convenient Location</h3>
                  <p className="text-gray-600">
                    Located in Countesthorpe, Leicestershire, we're easily accessible for customers throughout the region.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-bmw-blue font-semibold text-sm uppercase tracking-wide mb-2">See Our Work</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-4">
              Quality <span className="text-bmw-blue">That Speaks</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
              Browse our before and after gallery to see the exceptional quality of our repairs.
            </p>
          </div>
          <BeforeAfterGallery beforeAfterPairs={beforeAfterPairs} title="" />
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-navy mb-4">
              Customer <span className="text-bmw-blue">Reviews</span>
            </h2>
          </div>
          <Reviews limit={6} showTrustpilot={true} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your vehicle repair needs. We're here to help restore your car to perfect condition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
